"use client";

import { useMemo, useState } from "react";
import { Search, Plus, Pencil, Trash2, MoreHorizontal } from "lucide-react";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/lib/toast";
import CustomEmojiUpload from "@/components/tailwind/common/CustomEmojiUpload";
import {
  AnchoredPopover,
  SelfAnchoredPopover,
} from "@/components/tailwind/ui/anchored-popover";
import DeleteConfirmationModal from "@/components/tailwind/ui/deleteConfirmationModal";
import { deleteCustomEmoji } from "@/services-frontend/workspace/customEmojiServices";
import type { CustomEmoji } from "@/types/workspace";

/**
 * Settings → Workspace → Emoji: management home for workspace custom emojis.
 * The emoji picker itself is selection-only.
 */
export default function EmojisContent() {
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceContext();
  const { user } = useAuth();

  const customEmojis: CustomEmoji[] = currentWorkspace?.customEmojis ?? [];

  // Rename/delete is limited to the emoji's creator, the workspace owner, or
  // an admin — mirrors the backend permission check.
  const userId = user?.id ? String(user.id) : "";
  const userEmailLower = (user?.email || "").toLowerCase();
  const myMember = (currentWorkspace?.members || []).find(
    (m) => (m.userEmail || "").toLowerCase() === userEmailLower || String(m.userId) === userId,
  );
  const isWorkspaceOwner =
    !!userId && !!currentWorkspace?.ownerId && String(currentWorkspace.ownerId) === userId;
  const isWorkspaceAdmin = myMember?.role === "admin" || myMember?.role === "owner";
  const canManageEmoji = (emoji: CustomEmoji) =>
    isWorkspaceOwner || isWorkspaceAdmin || String(emoji.createdBy) === userId;

  const [searchTerm, setSearchTerm] = useState("");

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [menu, setMenu] = useState<{ emoji: CustomEmoji; anchor: HTMLElement } | null>(null);
  const [editTarget, setEditTarget] = useState<CustomEmoji | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomEmoji | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openMenu = (emoji: CustomEmoji, e: React.MouseEvent<HTMLButtonElement>) => {
    const anchor = e.currentTarget;
    setMenu((prev) => (prev?.emoji._id === emoji._id ? null : { emoji, anchor }));
  };

  const handleConfirmDelete = async () => {
    if (!currentWorkspace?._id || !deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteCustomEmoji(currentWorkspace._id, deleteTarget._id);
      setCurrentWorkspace({
        ...currentWorkspace,
        customEmojis: (currentWorkspace.customEmojis || []).filter((e) => e._id !== deleteTarget._id),
      } as typeof currentWorkspace);
      setDeleteTarget(null);
    } catch (err) {
      toast.error((err as Error).message || "Failed to delete emoji");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredEmojis = useMemo(() => {
    if (!searchTerm) return customEmojis;
    const q = searchTerm.toLowerCase();
    return customEmojis.filter((e) => e.name.toLowerCase().includes(q));
  }, [searchTerm, customEmojis]);

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="border-b border-component mb-4 mt-0 pb-3 text-base font-medium w-auto text-zinc-900 dark:text-zinc-100">
        Emoji
      </div>

      <div className="mt-2">
        <div className="border-b-0 mb-2 mt-0 pb-0 text-sm font-normal w-auto text-zinc-900 dark:text-zinc-100 flex justify-between items-center gap-2">
          <div className="flex flex-col flex-shrink overflow-hidden">
            Custom emoji
            <div className="text-xs leading-4 text-zinc-600 dark:text-zinc-400 whitespace-nowrap overflow-hidden text-ellipsis mt-0.75">
              Upload your own emojis — everyone in this workspace can use them from the emoji picker
            </div>
          </div>
          <div className="relative flex-shrink-0">
            <button
              role="button"
              tabIndex={0}
              title="Add a custom emoji"
              onClick={() => setIsAddOpen((open) => !open)}
              disabled={!currentWorkspace?._id}
              className="user-select-none transition-colors duration-200 ease-in cursor-pointer inline-flex items-center h-8 px-3 rounded-md whitespace-nowrap text-sm justify-center flex-shrink-0 bg-blue-500 dark:bg-blue-500 text-white fill-white leading-[1.2] font-medium hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-default"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add emoji
            </button>

            {isAddOpen && (
              <SelfAnchoredPopover
                placement="bottom-end"
                onClose={() => setIsAddOpen(false)}
                className="w-[300px] bg-white dark:bg-zinc-900 border border-component rounded-lg shadow-xl px-4"
              >
                <CustomEmojiUpload
                  showHeader
                  onCancel={() => setIsAddOpen(false)}
                  onSaved={() => setIsAddOpen(false)}
                />
              </SelfAnchoredPopover>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center mt-5 flex-wrap gap-4 gap-y-1.25">
          <div className="flex items-center w-full text-xs leading-4 relative rounded-full shadow-sm bg-zinc-50 dark:bg-zinc-800 cursor-text pt-1 pb-1 px-2.5 max-w-[250px] mr-auto">
            <div className="mr-1.5">
              <Search className="w-4 h-4 block text-zinc-500 dark:text-zinc-400 flex-shrink-0 mr-0.5 flex-grow-0" />
            </div>
            <input
              placeholder="Search emojis..."
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-xs leading-4 border-none bg-transparent w-full block resize-none p-0 text-zinc-900 dark:text-zinc-100 focus:outline-none"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-4">
          {/* Header */}
          <div className="flex relative border-t border-b border-component">
            <div className="w-[10%] min-w-[48px] px-1 flex items-center h-8">
              <div className="text-sm font-normal text-zinc-600 dark:text-zinc-400">Emoji</div>
            </div>
            <div className="w-[40%] min-w-[140px] px-1 flex items-center h-8">
              <div className="text-sm font-normal text-zinc-600 dark:text-zinc-400">Name</div>
            </div>
            <div className="w-[25%] min-w-[110px] px-1 flex items-center h-8">
              <div className="text-sm font-normal text-zinc-600 dark:text-zinc-400">Uploaded by</div>
            </div>
            <div className="w-[20%] min-w-[90px] px-1 flex items-center h-8">
              <div className="text-sm font-normal text-zinc-600 dark:text-zinc-400">Added</div>
            </div>
            <div className="w-[5%] min-w-[40px] px-1 flex items-center h-8" />
          </div>

          {/* Rows */}
          <div className="max-h-[400px] overflow-y-auto clean-scrollbar">
            {filteredEmojis.length === 0 ? (
              <div className="flex items-center justify-center h-24 text-sm text-zinc-500 dark:text-zinc-400 px-4 text-center">
                {customEmojis.length === 0
                  ? "No custom emojis yet. Click “Add emoji” to upload one."
                  : "No custom emojis match your search."}
              </div>
            ) : (
              filteredEmojis.map((emoji) => (
                <div
                  key={emoji._id}
                  className="flex items-center h-[46px] border-b border-zinc-100 dark:border-zinc-800 hover-component  transition-colors"
                >
                  <div className="w-[10%] min-w-[48px] px-1 flex items-center">
                    <img
                      loading="lazy"
                      src={emoji.imageUrl}
                      alt={emoji.name}
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <div className="w-[40%] min-w-[140px] px-1 flex items-center overflow-hidden">
                    <span className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                      {emoji.name}
                    </span>
                  </div>
                  <div className="w-[25%] min-w-[110px] px-1 flex items-center overflow-hidden">
                    <span className="text-sm text-zinc-600 dark:text-zinc-400 truncate">
                      {emoji.createdByName || "—"}
                    </span>
                  </div>
                  <div className="w-[20%] min-w-[90px] px-1 flex items-center">
                    <span className="text-sm text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
                      {formatDate(emoji.createdAt)}
                    </span>
                  </div>
                  <div className="w-[5%] min-w-[40px] px-1 flex items-center justify-end">
                    {canManageEmoji(emoji) && (
                      <button
                        type="button"
                        aria-label="Edit"
                        aria-haspopup="dialog"
                        aria-expanded={menu?.emoji._id === emoji._id}
                        onClick={(e) => openMenu(emoji, e)}
                        className="h-7 w-7 flex items-center justify-center rounded text-zinc-500 dark:text-zinc-400 hover-component  transition-colors"
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {menu && (
        <AnchoredPopover
          anchor={menu.anchor}
          placement="bottom-end"
          onClose={() => setMenu(null)}
          className="w-32 bg-white dark:bg-zinc-900 border border-component rounded-md shadow-lg py-1"
        >
          <button
            type="button"
            onClick={() => { setEditTarget(menu.emoji); setMenu(null); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-700 dark:text-zinc-300 hover-component  transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            type="button"
            onClick={() => { setDeleteTarget(menu.emoji); setMenu(null); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </AnchoredPopover>
      )}

      {/* Edit emoji: same panel as "Add custom emoji", in rename mode. */}
      {editTarget && (
        <div
          className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/50 dark:bg-black/70"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditTarget(null); }}
        >
          <div className="w-[300px] bg-white dark:bg-zinc-900 border border-component rounded-lg shadow-xl px-4 pb-1 max-h-[85dvh] overflow-y-auto">
            <CustomEmojiUpload
              showHeader
              editEmoji={editTarget}
              onCancel={() => setEditTarget(null)}
              onSaved={() => setEditTarget(null)}
            />
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        header="Delete emoji"
        message={deleteTarget ? `Are you sure you want to delete "${deleteTarget.name}"? It will no longer be available to anyone in this workspace.` : undefined}
        isDeleting={isDeleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
